import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import React, { useMemo, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import {
  CARC_CODES,
  GROUP_CODES,
  RARC_CODES,
  formatAdjustment,
} from "../../constants/adjustmentCodes";
import { RENDERING_TAXONOMY_OPTIONS } from "../../constants/renderingTaxonomies";
import { parseDate } from "../../helpers/date";
import {
  useGetClaimByIdQuery,
  useReprocessClaimMutation,
} from "../../store/queries/claims";
import {
  IClaim,
  ILineItem,
  ILineItemAdjustment,
  IMatched837Claim,
  DenialStatus,
  DENIAL_STATUS_LABELS,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  EFFECTIVE_YEAR_MISSING_CATEGORIES,
  TC_COMPONENT_VARIANT_CATEGORIES,
  MEDICARE_RATE_CAP_CATEGORIES,
} from "../../types/claim";
import { LoadingSpinner } from "../LoadingSpinner";
import { PrecalculatedAllowablesContent } from "../PrecalculatedAllowablesModal/PrecalculatedAllowablesContent";
import { AIInsightsTabContent } from "./AIInsightsTabContent";
import { shouldShow837CaseSignature } from "./caseSignature";
import { renderCaseSignatureWithHighlights } from "./caseSignatureDisplay";
import EpisodeEventTimeline from "./EpisodeEventTimeline";
import { EstimateComparisonTab } from "./EstimateComparisonTab";
import { NCCIBypassToggle } from "./NCCIBypassToggle";
import { PayerSelector } from "./PayerSelector";
import { RelevantContractLanguageAccordion } from "./RelevantContractLanguageAccordion";
import { SimilarClaimsSection } from "./SimilarClaimsSection";

interface IClaimDetailsModalProps {
  visible: boolean;
  onHide: () => void;
  claim: IClaim;
  accountId: string;
  onNavigateToClaim?: (claimId: string) => void;
  onBackToClaim?: () => void;
  isNavigating?: boolean;
  onClaimUpdated?: (claim: IClaim) => void;
}

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailRow = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  min-width: 150px;
  margin-right: 0.75rem;
  color: #495057;
`;

const DetailValue = styled.div`
  color: #212529;
  flex: 1;
`;

const PlaceholderText = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

const ScrollableTabContent = styled.div`
  height: 60vh;
  max-height: 650px;
  overflow-y: auto;
  padding: 0;
`;

const InfoIcon = styled.i`
  font-size: 0.75rem;
  color: #6c757d;
  margin-left: 4px;
  cursor: help;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: #495057;
  }
`;

const GlossarySection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const GlossaryTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`;

const GlossaryItem = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  line-height: 1.4;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GlossaryCode = styled.span`
  font-weight: 600;
  min-width: 60px;
  color: #212529;
`;

const GlossaryDescription = styled.span`
  color: #495057;
  flex: 1;
`;

const AdjustmentsCell = styled.div`
  font-size: 0.85rem;
  line-height: 1.4;
`;

const AdjustmentLine = styled.div`
  white-space: nowrap;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const StyledDialog = styled(Dialog)`
  /* PrimeReact pins the close icon to the top (align-self: flex-start).
     Center it so it lines up with the header title even when the header
     content is taller than one line (e.g. the Reprocess button). */
  .p-dialog-header-icons {
    align-self: center;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackButton = styled(Button)`
  &.p-button-text {
    padding: 0.25rem 0.5rem;
  }
`;

const ModalContentWrapper = styled.div`
  position: relative;
`;

const PATIENT_CLASS_STYLES: Record<
  string,
  { bg: string; color: string; label: string }
> = {
  INPATIENT: { bg: "#e8eaf6", color: "#3f51b5", label: "Inpatient" },
  OUTPATIENT: { bg: "#e8f5e9", color: "#2e7d32", label: "Outpatient" },
  UNKNOWN: { bg: "#f5f5f5", color: "#757575", label: "Unknown" },
};

const FACILITY_TYPE_LABELS: Record<string, string> = {
  "01": "Pharmacy",
  "02": "Telehealth (Other)",
  "10": "Telehealth (Home)",
  "11": "Office",
  "12": "Home",
  "13": "Assisted Living",
  "14": "Group Home",
  "19": "Off Campus-Outpatient Hospital",
  "20": "Urgent Care",
  "21": "Inpatient Hospital",
  "22": "Outpatient Hospital",
  "23": "Emergency Room",
  "24": "Ambulatory Surgical Center",
  "31": "Skilled Nursing Facility",
  "32": "Nursing Facility",
  "34": "Hospice",
  "41": "Ambulance - Land",
  "42": "Ambulance - Air/Water",
  "49": "Independent Clinic",
  "51": "Inpatient Psychiatric",
  "52": "Psychiatric Partial Hospitalization",
  "53": "Community Mental Health Center",
  "56": "Psychiatric Residential Treatment",
  "61": "Inpatient Rehab",
  "62": "Outpatient Rehab",
  "65": "ESRD Treatment Facility",
  "83": "Other Location",
  "99": "Other Unlisted Facility",
};

function formatFacilityType(code: string | null | undefined): string {
  if (!code) return "-";
  const label = FACILITY_TYPE_LABELS[code];
  return label ? `${code} - ${label}` : code;
}

// UB-04 Type of Bill — first two digits (facility type + bill classification). Used for
// institutional claims, where CLP08 carries the TOB prefix rather than a POS code (CLE-2235).
const TYPE_OF_BILL_LABELS: Record<string, string> = {
  "11": "Hospital - Inpatient (Part A)",
  "12": "Hospital - Inpatient (Part B)",
  "13": "Hospital - Outpatient",
  "14": "Hospital - Other (non-patient lab, etc.)",
  "18": "Hospital - Swing Bed",
  "21": "Skilled Nursing - Inpatient (Part A)",
  "22": "Skilled Nursing - Inpatient (Part B)",
  "23": "Skilled Nursing - Outpatient",
  "28": "Skilled Nursing - Swing Bed",
  "32": "Home Health - Inpatient (Part B)",
  "33": "Home Health - Outpatient",
  "34": "Home Health - Other (Part B)",
  "41": "Religious Nonmedical - Inpatient (Part A)",
  "71": "Clinic - Rural Health",
  "72": "Clinic - ESRD",
  "73": "Clinic - Freestanding",
  "74": "Clinic - Outpatient Rehab (ORF)",
  "75": "Clinic - CORF",
  "76": "Clinic - Community Mental Health Center",
  "81": "Special Facility - Hospice (non-hospital)",
  "82": "Special Facility - Hospice (hospital-based)",
  "83": "Special Facility - Ambulatory Surgery Center",
  "85": "Critical Access Hospital",
};

function formatTypeOfBill(tob: string | null | undefined): string {
  if (!tob) return "-";
  const prefix = tob.length >= 2 ? tob.slice(0, 2) : tob;
  const label = TYPE_OF_BILL_LABELS[prefix];
  return label ? `${tob} - ${label}` : tob;
}

// CLE-2437 — human-readable specialty for a rendering-provider taxonomy code (NM1*82 / PRV*PE*PXC
// from the matched 837). The curated option list is not exhaustive, so fall back to the raw code
// when the taxonomy isn't one of the known specialties (e.g. "2084P0800X — Psychiatry").
const TAXONOMY_LABELS: Record<string, string> = Object.fromEntries(
  RENDERING_TAXONOMY_OPTIONS.map((option) => [option.code, option.label])
);

function formatRenderingTaxonomy(code: string | null | undefined): string {
  if (!code) return "-";
  const label = TAXONOMY_LABELS[code];
  return label ? `${code} — ${label}` : code;
}

// Resolve which form-type interpretation to use for display. Prefer the stored claimFormType;
// for legacy/unreprocessed claims that still default to UNKNOWN, fall back to obvious
// institutional signals so an inpatient remit never renders a contradictory "Place of Service".
function resolveFormTypeForDisplay(
  claim: IClaim
): "INSTITUTIONAL" | "PROFESSIONAL" | "UNKNOWN" {
  if (claim.claimFormType && claim.claimFormType !== "UNKNOWN") {
    return claim.claimFormType;
  }
  if (claim.patientClass === "INPATIENT" || claim.drgCode || claim.typeOfBill) {
    return "INSTITUTIONAL";
  }
  return claim.facilityTypeCode ? "PROFESSIONAL" : "UNKNOWN";
}

const CLAIM_FORM_TYPE_LABELS: Record<string, string> = {
  INSTITUTIONAL: "Institutional (UB-04)",
  PROFESSIONAL: "Professional (CMS-1500)",
  UNKNOWN: "Unknown form type",
};

function PatientClassBadge({ patientClass }: { patientClass: string }) {
  const style =
    PATIENT_CLASS_STYLES[patientClass] || PATIENT_CLASS_STYLES.UNKNOWN;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.5rem",
        borderRadius: "4px",
        fontSize: "0.85rem",
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
      }}
    >
      {style.label}
    </span>
  );
}

// CLE-2225: denial status colors. Red = full denial, amber = partial denial.
const DENIAL_STATUS_STYLES: Record<
  Exclude<DenialStatus, "NONE">,
  { bg: string; color: string }
> = {
  FULL_DENIAL: { bg: "#f8d7da", color: "#842029" },
  PARTIAL_DENIAL: { bg: "#fff3cd", color: "#664d03" },
};

function DenialStatusBadge({ denialStatus }: { denialStatus: DenialStatus }) {
  if (denialStatus === "NONE") return null;
  const style = DENIAL_STATUS_STYLES[denialStatus];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.5rem",
        borderRadius: "4px",
        fontSize: "0.85rem",
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
      }}
    >
      {DENIAL_STATUS_LABELS[denialStatus]}
    </span>
  );
}

function formatDollars(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function InpatientClaimSummary({ claim }: { claim: IClaim }) {
  const summary = claim.claimSummaryData;
  const adjustments = summary?.adjustments || [];

  const sumAdjustments = (group: string, code?: string): number => {
    return adjustments
      .filter(
        (a) => a.group === group && (code === undefined || a.code === code)
      )
      .reduce((sum, a) => sum + a.amount, 0);
  };

  const contractAdjustment = sumAdjustments("CO");
  const deductible = sumAdjustments("PR", "1");
  const coinsurance = sumAdjustments("PR", "2");
  const copayment = sumAdjustments("PR", "3");

  // Build columns dynamically based on available data
  const columns: { header: string; value: string }[] = [
    {
      header: "Total Charges",
      value:
        claim.chargeAmount !== null
          ? formatDollars(claim.chargeAmount / 100)
          : "-",
    },
  ];

  columns.push({ header: "DRG", value: claim.drgCode || "-" });
  columns.push({
    header: "DRG Weight",
    value: summary?.drgWeight || "-",
  });
  columns.push({
    header: "DRG Amount",
    value: summary?.drgAmount != null ? formatDollars(summary.drgAmount) : "-",
  });
  columns.push({
    header: "LOS (Days)",
    value: summary?.lengthOfStay != null ? String(summary.lengthOfStay) : "-",
  });
  columns.push({
    header: "Discharge Status",
    value: claim.dischargeStatus || "-",
  });
  columns.push({
    header: "Covered Charges",
    value:
      summary?.coveredAmount != null
        ? formatDollars(summary.coveredAmount)
        : "-",
  });
  columns.push({
    header: "Contract Adj",
    value: contractAdjustment !== 0 ? formatDollars(contractAdjustment) : "-",
  });
  columns.push({
    header: "Deductible",
    value: deductible !== 0 ? formatDollars(deductible) : "-",
  });
  columns.push({
    header: "Coinsurance",
    value: coinsurance !== 0 ? formatDollars(coinsurance) : "-",
  });
  columns.push({
    header: "Copay",
    value: copayment !== 0 ? formatDollars(copayment) : "-",
  });
  columns.push({
    header: "Payment",
    value: claim.totalPaid != null ? formatDollars(claim.totalPaid / 100) : "-",
  });

  return (
    <DataTable
      value={[Object.fromEntries(columns.map((c) => [c.header, c.value]))]}
      style={{ fontSize: "0.9rem" }}
    >
      {columns.map((col) => (
        <Column key={col.header} field={col.header} header={col.header} />
      ))}
    </DataTable>
  );
}

const FREQUENCY_CODE_LABELS: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  "1": { label: "Original", bg: "#e3f2fd", color: "#1565c0" },
  "7": { label: "Replacement", bg: "#fff3e0", color: "#e65100" },
  "8": { label: "Voided", bg: "#ffebee", color: "#c62828" },
};

function getAuthoritativeReason(
  claim: IMatched837Claim,
  allClaims: IMatched837Claim[]
): string | null {
  if (!claim.isAuthoritative) return null;

  const hasReplacements = allClaims.some((c) => c.frequencyCode === "7");
  const hasVoids = allClaims.some((c) => c.frequencyCode === "8");
  const originals = allClaims.filter((c) => c.frequencyCode === "1");

  if (claim.frequencyCode === "7") {
    if (hasVoids) return "Latest replacement (voids excluded)";
    return "Replacement supersedes original";
  }

  if (originals.length > 1 && !hasReplacements) {
    return "Latest original submission";
  }

  if (allClaims.length === 1) return null; // Only one claim, no need to explain
  return "Selected as authoritative";
}

function Matched837ClaimsSection({ claims }: { claims: IMatched837Claim[] }) {
  // Sort: authoritative first, then voided last
  const sortedClaims = useMemo(
    () =>
      [...claims].sort((a, b) => {
        if (a.isAuthoritative !== b.isAuthoritative)
          return a.isAuthoritative ? -1 : 1;
        if (a.frequencyCode === "8" && b.frequencyCode !== "8") return 1;
        if (a.frequencyCode !== "8" && b.frequencyCode === "8") return -1;
        return 0;
      }),
    [claims]
  );

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3
        style={{
          margin: "0 0 0.75rem 0",
          fontSize: "16px",
          fontWeight: 600,
        }}
      >
        837 Claim Submission Data
      </h3>
      {sortedClaims.map((claim, idx) => {
        const freqStyle =
          FREQUENCY_CODE_LABELS[claim.frequencyCode] ||
          FREQUENCY_CODE_LABELS["1"];
        const isVoided = claim.frequencyCode === "8";
        const authReason = getAuthoritativeReason(claim, claims);

        return (
          <div
            key={idx}
            style={{
              marginBottom: "1rem",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
              overflow: "hidden",
              opacity: isVoided ? 0.6 : 1,
            }}
          >
            {/* Header bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 0.75rem",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e9ecef",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  padding: "0.15rem 0.5rem",
                  borderRadius: "4px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  backgroundColor: freqStyle.bg,
                  color: freqStyle.color,
                }}
              >
                {freqStyle.label}
              </span>
              {claim.isAuthoritative && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.35rem",
                    padding: "0.15rem 0.5rem",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                  }}
                >
                  Authoritative
                  {authReason && (
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        opacity: 0.8,
                      }}
                    >
                      ({authReason})
                    </span>
                  )}
                </span>
              )}
              <span
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  marginLeft: "auto",
                }}
              >
                Claim #{claim.claimNumber}
              </span>
            </div>

            {/* Details grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "0.5rem 1.5rem",
                padding: "0.75rem",
                fontSize: "0.85rem",
                textDecoration: isVoided ? "line-through" : "none",
              }}
            >
              <div>
                <span style={{ color: "#6c757d" }}>Service Date</span>
                <div style={{ fontWeight: 500 }}>
                  {claim.serviceDate || "-"}
                </div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Total Charges</span>
                <div style={{ fontWeight: 500 }}>
                  {formatDollars(claim.chargeAmount)}
                </div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Type of Bill</span>
                <div style={{ fontWeight: 500 }}>{claim.typeOfBill || "-"}</div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Patient Class</span>
                <div style={{ fontWeight: 500 }}>{claim.patientClass}</div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>DRG Code</span>
                <div style={{ fontWeight: 500 }}>{claim.drgCode || "-"}</div>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Provider NPI</span>
                <div style={{ fontWeight: 500 }}>
                  {claim.providerNPI || "-"}
                </div>
              </div>
            </div>

            {/* Service lines table — serviceLines may be undefined for a matched
                837 claim with no lines after 835↔837 matching/reprocessing (CLE-2483/CLE-2489) */}
            {claim.serviceLines && claim.serviceLines.length > 0 && (
              <div style={{ padding: "0 0.75rem 0.75rem" }}>
                <DataTable
                  value={claim.serviceLines}
                  size="small"
                  style={{ fontSize: "0.85rem" }}
                >
                  <Column field="code" header="Code" />
                  <Column
                    field="revenueCode"
                    header="Rev Code"
                    body={(row) => row.revenueCode || "-"}
                  />
                  <Column
                    field="modifier"
                    header="Modifier"
                    body={(row) => row.modifier || "-"}
                  />
                  <Column field="quantity" header="Qty" />
                  <Column
                    field="chargeAmount"
                    header="Charge"
                    body={(row) =>
                      row.chargeAmount != null
                        ? formatDollars(row.chargeAmount)
                        : "-"
                    }
                  />
                </DataTable>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Modal component to display comprehensive claim details
 * with three tabs: Claim Details, Allowable Calculations, and AI
 */
export function ClaimDetailsModal({
  visible,
  onHide,
  claim: initialClaim,
  accountId,
  onNavigateToClaim,
  onBackToClaim,
  isNavigating = false,
  onClaimUpdated,
}: IClaimDetailsModalProps) {
  const [reprocessClaim, { isLoading: isReprocessing }] =
    useReprocessClaimMutation();
  const toastRef = useRef<Toast>(null);
  const [showConfirmReprocess, setShowConfirmReprocess] = useState(false);

  // Local state for claim data to handle updates (e.g., NCCI bypass toggle)
  const [claim, setClaim] = useState<IClaim>(initialClaim);
  const [ncciBypassRecalculating, setNcciBypassRecalculating] = useState(false);
  // True once the by-id fetch has merged into local state for the current claim.
  // The list row (initialClaim) carries the raw stored precalc, but the by-id
  // response resolves the auto-NCCI-bypass-aware allowable (CLE-2346). Until the
  // merge lands, the Expected Amounts tab would paint the stale list value and
  // then visibly flip to the resolved value, so we gate that tab on this flag.
  const [fullClaimMerged, setFullClaimMerged] = useState(false);

  // Sync local state when initialClaim prop changes
  useEffect(() => {
    setClaim(initialClaim);
    setFullClaimMerged(false);
  }, [initialClaim.id]);

  // CLE-2166: list rows don't include childEvents (too expensive on the list query),
  // so the modal fetches the full claim by id when it opens. Once the response
  // arrives, the Event Timeline can render for multi-event episodes and the
  // Expected Amounts tab can use episodeNetLineItems.
  const { data: fullClaim, isError: isFullClaimError } = useGetClaimByIdQuery(
    { claimId: initialClaim.id },
    { skip: !visible || !initialClaim.id }
  );

  useEffect(() => {
    if (fullClaim && fullClaim.id === initialClaim.id) {
      setClaim((prev) => ({ ...prev, ...fullClaim }));
      setFullClaimMerged(true);
    }
  }, [fullClaim, initialClaim.id]);

  // The Expected Amounts breakdown must reflect the by-id (bypass-aware) claim.
  // Render it only once that data has merged; on a fetch error, fall back to the
  // list row rather than spinning forever.
  const expectedAmountsReady = fullClaimMerged || isFullClaimError;

  // Handle claim updates from child components
  const handleClaimUpdated = (updatedClaim: IClaim) => {
    setClaim(updatedClaim);
    // Propagate to parent component (e.g., ClaimsList)
    onClaimUpdated?.(updatedClaim);
  };

  // Ref for scrollable content to auto-scroll to top on claim change
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to top when claim changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [claim.id]);

  const handleReprocess = () => {
    setShowConfirmReprocess(true);
  };

  const handleConfirmReprocess = async () => {
    setShowConfirmReprocess(false);
    try {
      const result = await reprocessClaim(claim.id).unwrap();
      toastRef.current?.show({
        severity: "success",
        summary: "Claim Reprocessed",
        detail: "Claim has been reprocessed with updated calculations.",
        life: 5000,
      });
      if (onNavigateToClaim && result.data?.id) {
        onNavigateToClaim(result.data.id);
      }
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Reprocess Failed",
        detail: error?.data?.messages?.[0] || "Failed to reprocess claim",
        life: 8000,
      });
    }
  };

  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Format date helper
  const formatDate = (date: Date | null): string => {
    return date ? parseDate(date) : "-";
  };

  // Line items data.
  // CLE-2166: episode rows expose post-aggregation lines via episodeNetLineItems; pre-CLE-2166
  // flat claims use lineItems directly. The Expected Amounts tab drives off whichever is set.
  const lineItems: ILineItem[] = useMemo(() => {
    if (claim.isEpisodeRoot && claim.episodeNetLineItems) {
      return claim.episodeNetLineItems;
    }
    return claim.lineItems || [];
  }, [claim.isEpisodeRoot, claim.episodeNetLineItems, claim.lineItems]);

  // Line item templates
  const currencyBodyTemplate = (rowData: ILineItem, field: keyof ILineItem) => {
    const value = rowData[field];
    if (typeof value === "number") {
      return formatCurrency(Math.round(value * 100)); // Convert dollars to cents
    }
    return "-";
  };

  // Get deductible amount (PR-1) from adjustments
  const getDeductibleAmount = (rowData: ILineItem): string => {
    const adj = rowData.adjustments?.find(
      (a) => a.group === "PR" && a.code === "1"
    );
    return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
  };

  // Get coinsurance amount (PR-2) from adjustments
  const getCoinsuranceAmount = (rowData: ILineItem): string => {
    const adj = rowData.adjustments?.find(
      (a) => a.group === "PR" && a.code === "2"
    );
    return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
  };

  // Get copay amount (PR-3) from adjustments
  const getCopayAmount = (rowData: ILineItem): string => {
    const adj = rowData.adjustments?.find(
      (a) => a.group === "PR" && a.code === "3"
    );
    return adj && adj.amount > 0 ? `$${adj.amount.toFixed(2)}` : "-";
  };

  // Get all adjustments except PR-1 (deductible), PR-2 (coinsurance), PR-3 (copay)
  // These are shown in separate columns, so we exclude them from the Adjustments column
  const getOtherAdjustments = (rowData: ILineItem): ILineItemAdjustment[] => {
    return (
      rowData.adjustments?.filter(
        (adj) => !(adj.group === "PR" && ["1", "2", "3"].includes(adj.code))
      ) || []
    );
  };

  // Render adjustments cell (all adjustments except deductible/coinsurance/copay)
  // Also includes remark codes on separate lines
  const adjustmentsTemplate = (rowData: ILineItem) => {
    const otherAdjs = getOtherAdjustments(rowData);
    const remarks = rowData.remarks || [];

    if (otherAdjs.length === 0 && remarks.length === 0) return "-";

    return (
      <AdjustmentsCell>
        {otherAdjs.map((adj, idx) => (
          <AdjustmentLine key={`adj-${idx}`}>
            {formatAdjustment(adj.group, adj.code, adj.amount)}
          </AdjustmentLine>
        ))}
        {remarks.map((rem, idx) => {
          const description = RARC_CODES[rem.code];
          return (
            <AdjustmentLine key={`rem-${idx}`} style={{ color: "#6c757d" }}>
              {rem.code}
              {description ? ` — ${description}` : ""}
            </AdjustmentLine>
          );
        })}
      </AdjustmentsCell>
    );
  };

  // Collect all unique adjustment codes used across all line items for glossary
  const glossaryItems = useMemo(() => {
    const codeMap = new Map<string, { group: string; code: string }>();

    lineItems.forEach((item) => {
      item.adjustments?.forEach((adj) => {
        const key = `${adj.group}-${adj.code}`;
        if (!codeMap.has(key)) {
          codeMap.set(key, { group: adj.group, code: adj.code });
        }
      });
    });

    // Sort by group then code
    return Array.from(codeMap.values()).sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      return a.code.localeCompare(b.code);
    });
  }, [lineItems]);

  // Collect all unique remark codes for glossary
  const remarkItems = useMemo(() => {
    const remarkSet = new Set<string>();

    lineItems.forEach((item) => {
      item.remarks?.forEach((rem) => {
        remarkSet.add(rem.code);
      });
    });

    return Array.from(remarkSet).sort();
  }, [lineItems]);

  const renderLineItemsSection = () => {
    const showRemitSummary =
      claim.patientClass === "INPATIENT" && claim.claimSummaryData;
    // CLE-2275 — claim-level (header) CAS adjustments, e.g. OA-23 on a header-only
    // secondary DRG remit. Surfaced for any claim that carries them, not just inpatient.
    const claimLevelAdjustments = claim.claimSummaryData?.adjustments ?? [];

    return (
      <>
        {/* Always show Remit Summary for inpatient claims with summary data */}
        {showRemitSummary && (
          <>
            <h3
              style={{
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Remit Summary
            </h3>
            <InpatientClaimSummary claim={claim} />
          </>
        )}

        {/* CLE-2275 — claim-level adjustments (e.g. OA-23 COB on header-only remits) */}
        {claimLevelAdjustments.length > 0 && (
          <>
            <h3
              style={{
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Claim-Level Adjustments
            </h3>
            <DataTable
              value={claimLevelAdjustments}
              responsiveLayout="scroll"
              emptyMessage="No claim-level adjustments"
              style={{ fontSize: "0.9rem" }}
            >
              <Column
                header="Code"
                body={(adj) => `${adj.group}-${adj.code}`}
                style={{ minWidth: "6rem" }}
              />
              <Column
                header="Description"
                body={(adj) => CARC_CODES[adj.code] || "-"}
                style={{ minWidth: "16rem" }}
              />
              <Column
                header="Amount"
                body={(adj) => formatDollars(adj.amount)}
                style={{ minWidth: "7rem" }}
              />
            </DataTable>
          </>
        )}

        {/* Show Line Items table if line items exist */}
        {lineItems.length > 0 && (
          <>
            <h3
              style={{
                marginTop: showRemitSummary ? "1.5rem" : "1.5rem",
                marginBottom: "1rem",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Combined Line Items
            </h3>
            <DataTable
              value={lineItems}
              stripedRows
              responsiveLayout="scroll"
              emptyMessage="No line items available"
              style={{ fontSize: "0.9rem" }}
            >
              <Column
                field="cptCode"
                header="Code"
                style={{ minWidth: "5rem" }}
              />
              <Column
                field="modifier"
                header="Mod"
                body={(rowData) => rowData.modifier || "-"}
                style={{ minWidth: "4rem" }}
              />
              <Column
                field="revenueCode"
                header="Rev"
                body={(rowData) => rowData.revenueCode || "-"}
                style={{ minWidth: "4rem" }}
              />
              <Column
                field="billedUnits"
                header="Units"
                style={{ minWidth: "4rem" }}
              />
              <Column
                field="lineCharge"
                header="Billed"
                body={(rowData) => currencyBodyTemplate(rowData, "lineCharge")}
                style={{ minWidth: "6rem" }}
              />
              <Column
                field="lineAllowed"
                header="Allowed"
                body={(rowData) => currencyBodyTemplate(rowData, "lineAllowed")}
                style={{ minWidth: "6rem" }}
              />
              <Column
                header="Deduct"
                body={getDeductibleAmount}
                style={{ minWidth: "5rem" }}
              />
              <Column
                header="Coins"
                body={getCoinsuranceAmount}
                style={{ minWidth: "5rem" }}
              />
              <Column
                header="Copay"
                body={getCopayAmount}
                style={{ minWidth: "5rem" }}
              />
              <Column
                header="Adjustments"
                body={adjustmentsTemplate}
                style={{ minWidth: "10rem" }}
              />
              <Column
                field="linePaid"
                header="Paid"
                body={(rowData) => currencyBodyTemplate(rowData, "linePaid")}
                style={{ minWidth: "6rem" }}
              />
            </DataTable>

            {/* Glossary Section - shows explanations for codes used in this claim */}
            {glossaryItems.length > 0 && (
              <GlossarySection>
                <GlossaryTitle>Adjustment Codes</GlossaryTitle>
                {glossaryItems.map(({ group, code }) => {
                  const description = CARC_CODES[code];
                  const groupDesc = GROUP_CODES[group];
                  return (
                    <GlossaryItem key={`${group}-${code}`}>
                      <GlossaryCode>
                        {group}-{code}
                      </GlossaryCode>
                      <GlossaryDescription>
                        {description || `${groupDesc || group} adjustment`}
                      </GlossaryDescription>
                    </GlossaryItem>
                  );
                })}

                {/* Remark codes if any */}
                {remarkItems.length > 0 && (
                  <>
                    <GlossaryTitle style={{ marginTop: "1rem" }}>
                      Remark Codes
                    </GlossaryTitle>
                    {remarkItems.map((code) => {
                      const description = RARC_CODES[code];
                      return (
                        <GlossaryItem key={code}>
                          <GlossaryCode>{code}</GlossaryCode>
                          <GlossaryDescription>
                            {description || "Remark code"}
                          </GlossaryDescription>
                        </GlossaryItem>
                      );
                    })}
                  </>
                )}
              </GlossarySection>
            )}
          </>
        )}

        {/* Show placeholder if no summary data and no line items */}
        {!showRemitSummary && lineItems.length === 0 && (
          <>
            <h3
              style={{
                marginTop: "1.5rem",
                marginBottom: "1rem",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Combined Line Items
            </h3>
            <PlaceholderText>No line item data available</PlaceholderText>
          </>
        )}
      </>
    );
  };

  // Custom header with back button when navigation history exists
  const dialogHeader = (
    <ModalHeader>
      {onBackToClaim && (
        <BackButton
          icon="pi pi-arrow-left"
          className="p-button-text p-button-secondary"
          onClick={onBackToClaim}
          tooltip="Back to previous claim"
          tooltipOptions={{ position: "bottom" }}
        />
      )}
      <span>
        Remit Details - <span data-pii="patient-name">{claim.patientName}</span>
        {claim.claimNumber ? ` (${claim.claimNumber})` : ""}
      </span>
      <Button
        icon="pi pi-refresh"
        label="Reprocess"
        className="p-button-text p-button-warning p-button-sm mr-2"
        onClick={handleReprocess}
        loading={isReprocessing}
        tooltip="Re-parse from original EDI file"
        tooltipOptions={{ position: "bottom" }}
        style={{ marginLeft: "auto" }}
      />
    </ModalHeader>
  );

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Confirm Reprocess"
        visible={showConfirmReprocess}
        onHide={() => setShowConfirmReprocess(false)}
        style={{ width: "450px" }}
        modal
        footer={
          <div>
            <Button
              label="No"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowConfirmReprocess(false)}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              onClick={handleConfirmReprocess}
            />
          </div>
        }
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
          <i
            className="pi pi-refresh"
            style={{ fontSize: "1.5rem", color: "#f59e0b", marginTop: "2px" }}
          />
          <span>
            This will re-parse this claim from the original EDI file with
            current payer mappings and fee schedules. AI analysis and resolution
            status will be reset.
          </span>
        </div>
      </Dialog>
      <StyledDialog
        header={dialogHeader}
        visible={visible}
        onHide={onHide}
        style={{ width: "95vw", maxWidth: "1800px" }}
        modal
        dismissableMask
      >
        <ModalContentWrapper>
          {(isNavigating || isReprocessing) && (
            <LoadingOverlay>
              <div style={{ textAlign: "center" }}>
                <ProgressSpinner
                  style={{ width: "50px", height: "50px" }}
                  strokeWidth="4"
                />
                {isReprocessing && (
                  <div
                    style={{
                      marginTop: "0.75rem",
                      color: "#495057",
                      fontSize: "0.9rem",
                    }}
                  >
                    Reprocessing claim...
                  </div>
                )}
              </div>
            </LoadingOverlay>
          )}
          <TabView>
            {/* Tab 1: Remit Details */}
            <TabPanel header="Remit Details">
              <ScrollableTabContent ref={scrollContainerRef}>
                <div style={{ padding: "1rem" }}>
                  <Tooltip
                    target=".adj-code-tooltip"
                    position="top"
                    showDelay={0}
                    style={{ zIndex: 10000 }}
                  />
                  <DetailGrid>
                    {/* Left Column - Patient Information */}
                    <div>
                      <h3
                        style={{
                          marginTop: 0,
                          marginBottom: "1rem",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        Patient Information
                      </h3>
                      <DetailRow>
                        <DetailLabel>Patient Name:</DetailLabel>
                        <DetailValue data-pii="patient-name">
                          {claim.patientName}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Patient DOB:</DetailLabel>
                        <DetailValue>
                          {formatDate(claim.patientDOB)}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Account #:</DetailLabel>
                        <DetailValue>
                          {claim.patientAccountNumber || "-"}
                        </DetailValue>
                      </DetailRow>

                      <h3
                        style={{
                          marginTop: "1.5rem",
                          marginBottom: "1rem",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        Remit Information
                      </h3>
                      <DetailRow>
                        <DetailLabel>Claim #:</DetailLabel>
                        <DetailValue>{claim.claimNumber || "-"}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Service Date:</DetailLabel>
                        <DetailValue>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            {formatDate(claim.serviceDate)}
                            {/* Category badge — EFFECTIVE_YEAR_MISSING (data-quality warning),
                                TC_COMPONENT_VARIANT (CLE-2462) and MEDICARE_RATE_CAP (CLE-2489),
                                both false-positive underpayments. All rendered generically from
                                CATEGORY_LABELS/COLORS. */}
                            {claim.category &&
                              (EFFECTIVE_YEAR_MISSING_CATEGORIES.includes(
                                claim.category
                              ) ||
                                TC_COMPONENT_VARIANT_CATEGORIES.includes(
                                  claim.category
                                ) ||
                                MEDICARE_RATE_CAP_CATEGORIES.includes(
                                  claim.category
                                )) && (
                                <Tag
                                  value={CATEGORY_LABELS[claim.category]}
                                  severity={CATEGORY_COLORS[claim.category]}
                                  icon={
                                    EFFECTIVE_YEAR_MISSING_CATEGORIES.includes(
                                      claim.category
                                    )
                                      ? "pi pi-exclamation-triangle"
                                      : "pi pi-info-circle"
                                  }
                                />
                              )}
                          </span>
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Payer Name:</DetailLabel>
                        <DetailValue>{claim.claimPayerName}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Plan Name:</DetailLabel>
                        <DetailValue>{claim.claimPlanName || "-"}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Network Status:</DetailLabel>
                        <DetailValue>
                          {claim.claimNetworkStatus || "-"}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Matched Payer:</DetailLabel>
                        <DetailValue>
                          <PayerSelector
                            claim={claim}
                            onClaimUpdated={handleClaimUpdated}
                            onNavigateToClaim={onNavigateToClaim}
                          />
                        </DetailValue>
                      </DetailRow>
                      {claim.patientClass && (
                        <DetailRow>
                          <DetailLabel>Remit Type:</DetailLabel>
                          <DetailValue>
                            <PatientClassBadge
                              patientClass={claim.patientClass}
                            />
                            {resolveFormTypeForDisplay(claim) !== "UNKNOWN" && (
                              <span
                                style={{
                                  marginLeft: "0.5rem",
                                  fontSize: "0.8rem",
                                  color: "#6c757d",
                                }}
                              >
                                {
                                  CLAIM_FORM_TYPE_LABELS[
                                    resolveFormTypeForDisplay(claim)
                                  ]
                                }
                              </span>
                            )}
                          </DetailValue>
                        </DetailRow>
                      )}
                      {/* CLP08 (facilityTypeCode) is a Type-of-Bill prefix on institutional
                          claims and a CMS Place-of-Service code on professional claims, so the
                          row is labeled/formatted by claimFormType rather than always shown as
                          "Place of Service" (which mislabeled inpatient remits as e.g. "Office"
                          — CLE-2235). */}
                      {(() => {
                        const formType = resolveFormTypeForDisplay(claim);
                        if (
                          formType === "INSTITUTIONAL" &&
                          (claim.typeOfBill || claim.facilityTypeCode)
                        ) {
                          return (
                            <DetailRow>
                              <DetailLabel>Type of Bill:</DetailLabel>
                              <DetailValue>
                                {formatTypeOfBill(
                                  claim.typeOfBill || claim.facilityTypeCode
                                )}
                              </DetailValue>
                            </DetailRow>
                          );
                        }
                        if (
                          formType === "PROFESSIONAL" &&
                          claim.facilityTypeCode
                        ) {
                          return (
                            <DetailRow>
                              <DetailLabel>Place of Service:</DetailLabel>
                              <DetailValue>
                                {formatFacilityType(claim.facilityTypeCode)}
                              </DetailValue>
                            </DetailRow>
                          );
                        }
                        if (claim.facilityTypeCode) {
                          return (
                            <DetailRow>
                              <DetailLabel>Facility Code (CLP08):</DetailLabel>
                              <DetailValue>
                                {claim.facilityTypeCode}
                              </DetailValue>
                            </DetailRow>
                          );
                        }
                        return null;
                      })()}
                      {(claim.renderingProviderNPI || claim.providerNPI) && (
                        <DetailRow>
                          <DetailLabel>NPI:</DetailLabel>
                          <DetailValue>
                            {claim.renderingProviderNPI || claim.providerNPI}
                          </DetailValue>
                        </DetailRow>
                      )}
                      {claim.renderingTaxonomy && (
                        <DetailRow>
                          <DetailLabel>Taxonomy:</DetailLabel>
                          <DetailValue>
                            {formatRenderingTaxonomy(claim.renderingTaxonomy)}
                          </DetailValue>
                        </DetailRow>
                      )}
                    </div>

                    {/* Right Column - Financial Summary */}
                    <div>
                      <h3
                        style={{
                          marginTop: 0,
                          marginBottom: "1rem",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        Financial Summary
                      </h3>
                      {/* CLE-2266: when the 837 (billed) signature differs from the 835
                          (adjudicated) one we show both rows; in that case label the first row
                          "(835)" so the two line up and are unambiguous. When there's no 837
                          row, the plain "Case Signature:" label is sufficient. */}
                      <DetailRow>
                        <DetailLabel>
                          {shouldShow837CaseSignature(
                            claim.caseSignature,
                            claim.edi837CaseSignature
                          )
                            ? "Case Signature (835):"
                            : "Case Signature:"}
                        </DetailLabel>
                        <DetailValue>{claim.caseSignature}</DetailValue>
                      </DetailRow>
                      {shouldShow837CaseSignature(
                        claim.caseSignature,
                        claim.edi837CaseSignature
                      ) && (
                        <DetailRow>
                          <DetailLabel>Case Signature (837):</DetailLabel>
                          <DetailValue>
                            {renderCaseSignatureWithHighlights(
                              claim.edi837CaseSignature || "",
                              claim.caseSignature || ""
                            )}
                          </DetailValue>
                        </DetailRow>
                      )}
                      <DetailRow>
                        <DetailLabel>Total Charges:</DetailLabel>
                        <DetailValue>
                          {formatCurrency(claim.chargeAmount)}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Remit Amount:</DetailLabel>
                        <DetailValue>
                          {formatCurrency(claim.claimAmount)}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Expected Amount:</DetailLabel>
                        <DetailValue>
                          {formatCurrency(claim.expectedAmount)}
                        </DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Difference:</DetailLabel>
                        <DetailValue
                          style={
                            claim.amountDifference && claim.amountDifference < 0
                              ? { fontWeight: "bold", color: "#dc3545" }
                              : undefined
                          }
                        >
                          {formatCurrency(claim.amountDifference)}
                        </DetailValue>
                      </DetailRow>
                      {/* CLE-2225: surface denial status next to the variance so an analyst
                          can see at a glance that an "underpayment" is really a denial. */}
                      {claim.denialStatus && claim.denialStatus !== "NONE" && (
                        <DetailRow>
                          <DetailLabel>Denial Status:</DetailLabel>
                          <DetailValue>
                            <DenialStatusBadge
                              denialStatus={claim.denialStatus}
                            />
                            {/* CLE-2225: the denial classification (code + title) that drove
                                the status. Hovering shows the classifier's reason. */}
                            {claim.denialClassificationCode && (
                              <>
                                <Tooltip
                                  target=".denial-classification-tooltip"
                                  position="top"
                                  showDelay={0}
                                  style={{ zIndex: 10000, maxWidth: "320px" }}
                                />
                                <span
                                  className={
                                    claim.denialClassificationReason
                                      ? "denial-classification-tooltip"
                                      : undefined
                                  }
                                  data-pr-tooltip={
                                    claim.denialClassificationReason ||
                                    undefined
                                  }
                                  style={{
                                    marginLeft: "0.5rem",
                                    fontSize: "0.85rem",
                                    color: "#495057",
                                    cursor: claim.denialClassificationReason
                                      ? "help"
                                      : "default",
                                  }}
                                >
                                  {claim.denialClassificationCode}
                                  {claim.denialClassificationTitle
                                    ? ` — ${claim.denialClassificationTitle}`
                                    : ""}
                                </span>
                              </>
                            )}
                          </DetailValue>
                        </DetailRow>
                      )}
                    </div>
                  </DetailGrid>

                  {renderLineItemsSection()}

                  {/* CLE-2166: per-event timeline for episode rows. Shown for any episode
                      root with at least one child event — including single-event episodes,
                      where the timeline still surfaces per-event metadata (remit status,
                      payer claim control number, BPR payment date, source file) that the
                      line items section above doesn't show. */}
                  {claim.isEpisodeRoot &&
                    Array.isArray(claim.childEvents) &&
                    claim.childEvents.length >= 1 && (
                      <EpisodeEventTimeline
                        events={claim.childEvents}
                        formatCurrency={formatCurrency}
                      />
                    )}

                  {/* 837 Claim Submission Data */}
                  {claim.matched837Claims &&
                    claim.matched837Claims.length > 0 && (
                      <Matched837ClaimsSection
                        claims={claim.matched837Claims}
                      />
                    )}

                  {/* Similar Correctly Paid Claims Section - only for claims with line items */}
                  {lineItems.length > 0 && (
                    <SimilarClaimsSection
                      claimId={claim.id}
                      onNavigateToClaim={onNavigateToClaim}
                    />
                  )}
                </div>
              </ScrollableTabContent>
            </TabPanel>

            {/* Tab 2: Allowable Calculations */}
            <TabPanel header="Expected Amounts">
              <div style={{ position: "relative" }}>
                {ncciBypassRecalculating && (
                  <LoadingOverlay style={{ zIndex: 11 }}>
                    <div style={{ textAlign: "center" }}>
                      <LoadingSpinner
                        customStyle={{ width: "60px", height: "60px" }}
                      />
                      <div
                        style={{
                          marginTop: "0.75rem",
                          color: "#495057",
                          fontSize: "1rem",
                          fontWeight: 600,
                        }}
                      >
                        Calculating adjustments...
                      </div>
                    </div>
                  </LoadingOverlay>
                )}
                <ScrollableTabContent>
                  {(() => {
                    // Gate on the by-id (bypass-aware) claim: the list row seeds
                    // the raw stored allowable, and painting it before the by-id
                    // merge causes a visible flip to the resolved value (CLE-2346).
                    if (!expectedAmountsReady) {
                      return (
                        <div
                          style={{ textAlign: "center", padding: "3rem 0" }}
                          data-testid="expected-amounts-loading"
                        >
                          <LoadingSpinner
                            customStyle={{ width: "60px", height: "60px" }}
                          />
                          <div
                            style={{
                              marginTop: "0.75rem",
                              color: "#495057",
                              fontSize: "1rem",
                              fontWeight: 600,
                            }}
                          >
                            Calculating adjustments...
                          </div>
                        </div>
                      );
                    }
                    const isTransferClaim =
                      claim.losOutlierCalculation?.ruleApplied &&
                      claim.losOutlierCalculation?.pricingRule ===
                        "BCBST_DRG_TRANSFER";
                    const has837Match = claim.matched837Claims?.some(
                      (c) => c.isAuthoritative && c.caseSignature
                    );

                    return (
                      <>
                        {/* NCCI Bypass Toggle */}
                        <NCCIBypassToggle
                          claimId={claim.id}
                          claim={claim}
                          onClaimUpdated={handleClaimUpdated}
                          onRecalculating={setNcciBypassRecalculating}
                        />

                        {/* 835-based expected amount — hidden when transfer applies */}
                        {!isTransferClaim && (
                          <>
                            {has837Match && (
                              <h4
                                style={{
                                  margin: "0.5rem 1rem 0.5rem",
                                  fontSize: "14px",
                                  color: "#495057",
                                }}
                              >
                                Expected Amount (835 Case Signature)
                              </h4>
                            )}
                            {/* Charge-Based Pricing always wins over the placeholder:
                                the rule IS the pricing for this claim, so the precalc
                                gate (payerId/expectedAmount) doesn't apply. PrecalculatedAllowablesContent
                                short-circuits internally and renders ChargeBasedPricingBlock. */}
                            {claim.chargeBasedPricingCalculation ||
                            (claim.payerId && claim.expectedAmount !== null) ? (
                              <PrecalculatedAllowablesContent
                                accountId={accountId}
                                payerId={claim.payerId ?? 0}
                                caseSignature={claim.caseSignature}
                                dateOfService={
                                  claim.serviceDate
                                    ? new Date(claim.serviceDate).toISOString()
                                    : undefined
                                }
                                claim={claim}
                                lineItems={lineItems}
                                // 835-derived override/overlay calculations.
                                // These mirror the 837 DRG branch below: an
                                // inpatient-ruleset / stop-loss / charge-outlier
                                // / pass-through claim with no 837 match renders
                                // here, so without these props the tab would
                                // show the un-overridden base allowable.
                                outlierPaymentInCents={
                                  claim.outlierPaymentInCents
                                }
                                outlierCalculation={claim.outlierCalculation}
                                passThroughCalculation={
                                  claim.passThroughCalculation
                                }
                                chargeAmountInCents={claim.chargeAmount}
                                losOutlierCalculation={
                                  claim.losOutlierCalculation
                                }
                                stopLossPaymentInCents={
                                  claim.stopLossPaymentInCents
                                }
                                stopLossCalculation={claim.stopLossCalculation}
                                chargeOutlierPaymentInCents={
                                  claim.chargeOutlierPaymentInCents
                                }
                                chargeOutlierCalculation={
                                  claim.chargeOutlierCalculation
                                }
                                inpatientOutlierCalculation={
                                  claim.inpatientOutlierCalculation
                                }
                                // The cap is computed on the claim's final
                                // expectedAmount, so it belongs to whichever
                                // block is the "final" one for this claim.
                                // When 837/DRG path also renders, surface the
                                // cap there only to avoid double-rendering.
                                lesserOfChargesCalculation={
                                  has837Match
                                    ? undefined
                                    : claim.lesserOfChargesCalculation
                                }
                              />
                            ) : (
                              <PlaceholderText>
                                Allowable calculations not available for this
                                claim.
                              </PlaceholderText>
                            )}
                          </>
                        )}

                        {/* 837 DRG-based expected amount */}
                        {has837Match && (
                          <>
                            <h4
                              style={{
                                margin: isTransferClaim
                                  ? "0.5rem 1rem 0.5rem"
                                  : "1.5rem 1rem 0.5rem",
                                fontSize: "14px",
                                color: "#495057",
                              }}
                            >
                              {isTransferClaim
                                ? "Expected Amount (DRG Transfer)"
                                : "Expected Amount (837 Case Signature)"}
                            </h4>
                            {(() => {
                              if (isTransferClaim && claim.payerId) {
                                return (
                                  <PrecalculatedAllowablesContent
                                    accountId={accountId}
                                    payerId={claim.payerId}
                                    caseSignature={
                                      claim.edi837CaseSignature ||
                                      claim.caseSignature
                                    }
                                    dateOfService={
                                      claim.serviceDate
                                        ? new Date(
                                            claim.serviceDate
                                          ).toISOString()
                                        : undefined
                                    }
                                    claim={claim}
                                    losOutlierCalculation={
                                      claim.losOutlierCalculation
                                    }
                                  />
                                );
                              }
                              if (
                                claim.edi837ExpectedAmount !== null &&
                                claim.edi837ExpectedAmount !== undefined &&
                                claim.payerId &&
                                (claim.edi837CaseSignature ||
                                  claim.caseSignature)
                              ) {
                                return (
                                  <PrecalculatedAllowablesContent
                                    accountId={accountId}
                                    payerId={claim.payerId}
                                    caseSignature={
                                      claim.edi837CaseSignature ||
                                      claim.caseSignature
                                    }
                                    dateOfService={
                                      claim.serviceDate
                                        ? new Date(
                                            claim.serviceDate
                                          ).toISOString()
                                        : undefined
                                    }
                                    claim={claim}
                                    outlierPaymentInCents={
                                      claim.outlierPaymentInCents
                                    }
                                    outlierCalculation={
                                      claim.outlierCalculation
                                    }
                                    passThroughCalculation={
                                      claim.passThroughCalculation
                                    }
                                    chargeAmountInCents={claim.chargeAmount}
                                    losOutlierCalculation={
                                      claim.losOutlierCalculation
                                    }
                                    stopLossPaymentInCents={
                                      claim.stopLossPaymentInCents
                                    }
                                    stopLossCalculation={
                                      claim.stopLossCalculation
                                    }
                                    chargeOutlierPaymentInCents={
                                      claim.chargeOutlierPaymentInCents
                                    }
                                    chargeOutlierCalculation={
                                      claim.chargeOutlierCalculation
                                    }
                                    inpatientOutlierCalculation={
                                      claim.inpatientOutlierCalculation
                                    }
                                    lesserOfChargesCalculation={
                                      claim.lesserOfChargesCalculation
                                    }
                                  />
                                );
                              }
                              return (
                                <PlaceholderText>
                                  DRG-based allowable calculations not
                                  available. No contract data found for this DRG
                                  code.
                                </PlaceholderText>
                              );
                            })()}
                          </>
                        )}

                        <RelevantContractLanguageAccordion claimId={claim.id} />
                      </>
                    );
                  })()}
                </ScrollableTabContent>
              </div>
            </TabPanel>

            {/* Tab 3: AI Insights */}
            <TabPanel header="AI Insights">
              <ScrollableTabContent>
                <AIInsightsTabContent
                  claimId={claim.id}
                  cachedAnalysis={claim.aiAnalysisResult || null}
                  promptVersion={claim.promptVersion}
                  paymentType={claim.paymentType}
                  resolutionStatus={claim.resolutionStatus}
                  resolutionHistory={claim.resolutionHistory}
                  claimData={{
                    patientName: claim.patientName,
                    claimNumber: claim.claimNumber,
                    serviceDate: claim.serviceDate,
                    claimPayerName: claim.claimPayerName,
                    chargeAmount: claim.chargeAmount,
                  }}
                />
              </ScrollableTabContent>
            </TabPanel>

            {/* Tab 4: Estimate Comparison */}
            <TabPanel header="Estimate Comparison">
              <ScrollableTabContent>
                <EstimateComparisonTab claim={claim} />
              </ScrollableTabContent>
            </TabPanel>
          </TabView>
        </ModalContentWrapper>
      </StyledDialog>
    </>
  );
}
